import type { ResourceType } from '__types__';
import type { ApiClient } from 'lib/api/ApiClient.types';
import { parseResourceType } from './resource-types.http.schema';
import type {
  ResourceTypeBindingsApi,
  ResourceTypeWorkspaceBinding,
  SetResourceTypeEnabledInput,
  SetResourceTypeWorkspaceFieldsInput
} from './resource-type-bindings.api';
import {
  flattenResourceTypeBindings,
  parseWorkspaceBindingJoinRow,
  parseWorkspaceRelationsFromTypeHttp,
  type ResourceTypeWorkspaceRelation
} from './resource-type-bindings.schema';

/**
 * Minimal hooks so this module stays free of Redux / admin sync imports.
 * Connected wires `readBindings` + `onBindingsSourceUpdated`; tests inject fakes.
 *
 * @see resource-type-bindings.api.connected.ts for why list reads nested
 * join extras from the v1 settings cache instead of a dedicated bindings GET.
 */
export type ResourceTypeBindingsHttpApiOptions = {
  /** Source for `list()` — flattened join extras (connected reads Redux once). */
  readBindings: () => ResourceTypeWorkspaceBinding[];
  /**
   * After a binding write + GET: push the typed type and freshly parsed join
   * rows into the list source. Join extras never travel on {@link ResourceType}.
   */
  onBindingsSourceUpdated: (
    type: ResourceType,
    workspaceRelations: ResourceTypeWorkspaceRelation[]
  ) => void;
};

export function createResourceTypeBindingsHttpApi(
  client: ApiClient,
  options: ResourceTypeBindingsHttpApiOptions
): ResourceTypeBindingsApi {
  async function refetchAndSync(resourceTypeId: number): Promise<{
    type: ResourceType;
    workspaceRelations: ResourceTypeWorkspaceRelation[];
  }> {
    const response = await client.get(`/resource_types/${resourceTypeId}`, {
      token: true
    });
    const type = parseResourceType(response);
    const workspaceRelations = parseWorkspaceRelationsFromTypeHttp(response);
    options.onBindingsSourceUpdated(type, workspaceRelations);
    return { type, workspaceRelations };
  }

  function bindingFromRelations(
    resourceTypeId: number,
    workspaceRelations: ResourceTypeWorkspaceRelation[],
    workspaceId: number,
    fallback: ResourceTypeWorkspaceBinding
  ): ResourceTypeWorkspaceBinding {
    return (
      flattenResourceTypeBindings([
        { id: resourceTypeId, enterprise_workspaces: workspaceRelations }
      ]).find(binding => binding.workspaceId === workspaceId) ?? fallback
    );
  }

  return {
    async list(_organizationId) {
      return options.readBindings();
    },

    async setEnabled(input: SetResourceTypeEnabledInput) {
      const raw = await client.put(
        `/resource_types/${input.resourceTypeId}/workspace-binding`,
        {
          data: {
            workspaceId: input.workspaceId,
            enabled: input.enabled
          },
          token: true
        }
      );
      const fromJoin = parseWorkspaceBindingJoinRow(raw);
      const { workspaceRelations } = await refetchAndSync(input.resourceTypeId);
      return bindingFromRelations(
        input.resourceTypeId,
        workspaceRelations,
        input.workspaceId,
        fromJoin
      );
    },

    async setWorkspaceFields(input: SetResourceTypeWorkspaceFieldsInput) {
      const raw = await client.put(
        `/resource_types/${input.resourceTypeId}/workspace-binding`,
        {
          data: {
            workspaceId: input.workspaceId,
            workspaceFields: input.workspaceFields
          },
          token: true
        }
      );
      const fromJoin = parseWorkspaceBindingJoinRow(raw);
      const { workspaceRelations } = await refetchAndSync(input.resourceTypeId);
      return bindingFromRelations(
        input.resourceTypeId,
        workspaceRelations,
        input.workspaceId,
        fromJoin
      );
    }
  };
}
